import { useEffect, useState } from 'react';
import get from 'lodash/get';
import type { IStateController } from '../lib/widget/state/state.interface';
import { useInjection } from 'inversify-react';
import { STATE_MODULE_IDS } from '../lib/widget/state/state.types';
import type { ITicketsController } from '../lib/widget/tickets/tickets.interface';
import { TICKETS_MODULE_IDS } from '../lib/widget/tickets/tickets.types';

const getBasketSize = (
  basketSizes,
  controllers: { ticketsController: ITicketsController; stateController: IStateController },
) => {
  const { Widget } = window;
  const { isFullscreen } = controllers.stateController.state;

  if (!isFullscreen) return 0;

  const isMobile = window.matchMedia('(max-width: 700px)').matches;
  const currentBasketSize = isMobile ? basketSizes.mobile : basketSizes.desktop;
  const hasSelectedTickets =
    controllers.ticketsController.selectedTickets.length > 0 ||
    get(Widget, 'settings.selectedTickets.length', 0) > 0;

  return hasSelectedTickets ? currentBasketSize : 0;
};

const useBasketSize = () => {
  const { Widget } = window;
  const ticketsController: ITicketsController = useInjection(TICKETS_MODULE_IDS.TICKETS_CONTROLLER);
  const stateController: IStateController = useInjection(STATE_MODULE_IDS.STATE_CONTROLLER);
  const basketSizes = get(Widget, 'settings.basketSizes');

  if (!basketSizes) return 0;

  const [basketSize, setBasketSize] = useState(
    getBasketSize(basketSizes, { ticketsController, stateController }),
  );

  const updateSize = () => {
    setBasketSize(getBasketSize(basketSizes, { ticketsController, stateController }));
  };

  useEffect(() => {
    ticketsController.addListener(ticketsController.events.CHANGE, updateSize);
    ticketsController.addListener(ticketsController.events.RENDER_TICKETS, updateSize);
    ticketsController.addListener(ticketsController.events.INIT_TICKETS, updateSize);
    ticketsController.addListener(ticketsController.events.REMOVE, updateSize);
    stateController.addListener(stateController.events.UPDATE, updateSize);
    window.addEventListener('orientationchange', updateSize);

    return () => {
      ticketsController.removeListener(ticketsController.events.CHANGE, updateSize);
      ticketsController.removeListener(ticketsController.events.RENDER_TICKETS, updateSize);
      ticketsController.removeListener(ticketsController.events.INIT_TICKETS, updateSize);
      ticketsController.removeListener(ticketsController.events.REMOVE, updateSize);
      stateController.removeListener(stateController.events.UPDATE, updateSize);
      window.removeEventListener('orientationchange', updateSize);
    };
  });

  return basketSize;
};

export default useBasketSize;
